import React, {createContext, useContext, useEffect, useState} from 'react';
import {FirebaseContext} from '../index';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signOut
} from 'firebase/auth';
import {doc, getDoc, setDoc} from 'firebase/firestore';
import {getFunctions, httpsCallable} from 'firebase/functions';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider = ({ children }) => {
  const { auth, db } = useContext(FirebaseContext);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [functions, setFunctions] = useState(null);
  const [googleAccessToken, setGoogleAccessToken] = useState(null);

  // Helper function to get institution from email
  const getInstitutionFromEmail = (email) => {
    return email.split('@')[1];
  };

  // Helper function to add/update user in college subcollection
  const addUserToCollegeCollection = async (user) => {
    try {
      const institution = getInstitutionFromEmail(user.email);
      const collegeUserRef = doc(db, 'colleges', institution, 'users', user.email);

      // Check if user already exists in college collection
      const collegeUserDoc = await getDoc(collegeUserRef);

      if (collegeUserDoc.exists()) {
        // User exists, just update lastSignIn
        await setDoc(collegeUserRef, {
          lastSignIn: new Date().toISOString()
        }, { merge: true });
      } else {
        // New user, create with default values
        await setDoc(collegeUserRef, {
          email: user.email,
          displayName: user.displayName || '',
          role: 'Student',
          modules: [],
          lastSignIn: new Date().toISOString(),
          createdAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error adding user to college collection:', error);
      // Don't throw - we don't want to block authentication if this fails
    }
  };

  useEffect(() => {
    // Initialize functions
    const functionsInstance = getFunctions();
    setFunctions(functionsInstance);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Email domain validation
        if (!user.email.endsWith('@middlebury.edu')) {
          await signOut(auth);
          setCurrentUser(null);
          setLoading(false);
          return;
        }

        // Add/update user in college collection
        await addUserToCollegeCollection(user);

        // Fetch additional user data for valid users
        const institution = getInstitutionFromEmail(user.email);
        const userDocRef = doc(db, 'colleges', institution, 'users', user.email);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCurrentUser({ ...user, ...userData });
          // Retrieve stored Google access token if available
          if (userData.googleAccessToken) {
            setGoogleAccessToken(userData.googleAccessToken);
          }
        } else {
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
        setGoogleAccessToken(null);
      }
      setLoading(false);
    });

    // Handle email sign-in link
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = localStorage.getItem('emailForSignIn');

      if (!email) {
        email = window.prompt('Please provide your email for confirmation');
      }

      if (email && email.endsWith('@middlebury.edu')) {
        signInWithEmailLink(auth, email, window.location.href)
          .then(async (result) => {
            // Add user to college collection after successful sign-in
            if (result.user) {
              await addUserToCollegeCollection(result.user);
            }
            localStorage.removeItem('emailForSignIn');
            window.history.replaceState(null, '', window.location.pathname);
          })
          .catch((error) => {
            console.error("Error signing in with email link:", error);
          });
      }
    }

    return unsubscribe;
  }, [auth, db]);

  const signInWithGoogle = async () => {
    const googleProvider = new GoogleAuthProvider();
    googleProvider.setCustomParameters({
      hd: 'middlebury.edu',
      prompt: 'consent',
      access_type: 'offline'
    });

    // Add Google Calendar scope
    googleProvider.addScope('https://www.googleapis.com/auth/calendar');
    googleProvider.addScope('https://www.googleapis.com/auth/calendar.events');

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Domain check
      if (!user.email.endsWith('.edu')) {
        await signOut(auth);
        throw new Error("Only educational institution email addresses are allowed.");
      }

      // Add user to college collection
      await addUserToCollegeCollection(user);

      // Get the OAuth credential
      const credential = GoogleAuthProvider.credentialFromResult(result);

      if (credential && credential.accessToken) {
        // Set the access token in state immediately
        setGoogleAccessToken(credential.accessToken);

        // Store the access token in Firestore for persistence
        const userDocRef = doc(db, 'users', user.uid);
        await setDoc(userDocRef, {
          email: user.email,
          displayName: user.displayName,
          lastSignIn: new Date().toISOString(),
          hasCalendarAccess: true,
          googleAccessToken: credential.accessToken, // Store locally for now
          calendarConnectedAt: new Date().toISOString()
        }, { merge: true });

        // Also send to backend for proper OAuth handling
        try {
          const storeToken = httpsCallable(functions, 'storeGoogleToken');
          await storeToken({ accessToken: credential.accessToken });
        } catch (tokenError) {
          console.error('Error storing token on backend:', tokenError);
          // Continue even if backend storage fails
        }
      }

      // Reload the user data to get the updated state
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        setCurrentUser({ ...user, ...userDoc.data() });
      }

      return result;
    } catch (error) {
      console.error("Google sign-in error:", error);
      throw error;
    }
  };

  // Email and password authentication
  const signup = async (email, password) => {
    if (!email.endsWith('@middlebury.edu')) {
      return Promise.reject(new Error("Only @middlebury.edu email addresses are allowed."));
    }

    const result = await createUserWithEmailAndPassword(auth, email, password);

    // Add user to college collection after successful signup
    if (result.user) {
      await addUserToCollegeCollection(result.user);
    }

    return result;
  };

  const login = async (email, password) => {
    if (!email.endsWith('@middlebury.edu')) {
      return Promise.reject(new Error("Only @middlebury.edu email addresses are allowed."));
    }

    const result = await signInWithEmailAndPassword(auth, email, password);

    // Add user to college collection after successful login
    if (result.user) {
      await addUserToCollegeCollection(result.user);
    }

    return result;
  };

  // Passwordless email authentication
  const loginWithEmail = (email) => {
    if (!email.endsWith('@middlebury.edu')) {
      return Promise.reject(new Error("Only @middlebury.edu email addresses are allowed."));
    }

    const actionCodeSettings = {
      url: window.location.origin + '/login',
      handleCodeInApp: true
    };

    localStorage.setItem('emailForSignIn', email);

    return sendSignInLinkToEmail(auth, email, actionCodeSettings)
      .then(() => {
        return { success: true, message: "Sign-in link sent to your email" };
      })
      .catch((error) => {
        console.error("Error sending sign-in link:", error);
        return Promise.reject(error);
      });
  };

  const logout = () => {
    setGoogleAccessToken(null);
    return signOut(auth);
  };

  // Calendar functions with proper token handling
  const getCalendarEvents = async (options = {}) => {
    if (!functions) throw new Error('Functions not initialized');

    const fetchEvents = httpsCallable(functions, 'getCalendarEvents');
    return fetchEvents({
      ...options,
      accessToken: googleAccessToken // Pass the access token if needed
    });
  };

  const createCalendarEvent = async (eventData) => {
    if (!functions) throw new Error('Functions not initialized');

    const createEvent = httpsCallable(functions, 'createCalendarEvent');
    return createEvent({
      event: eventData,
      accessToken: googleAccessToken // Pass the access token if needed
    });
  };

  const updateCalendarEvent = async (eventId, eventData) => {
    if (!functions) throw new Error('Functions not initialized');

    const updateEvent = httpsCallable(functions, 'updateCalendarEvent');
    return updateEvent({
      eventId,
      event: eventData,
      accessToken: googleAccessToken // Pass the access token if needed
    });
  };

  const deleteCalendarEvent = async (eventId) => {
    if (!functions) throw new Error('Functions not initialized');

    const deleteEvent = httpsCallable(functions, 'deleteCalendarEvent');
    return deleteEvent({
      eventId,
      accessToken: googleAccessToken
    });
  };

  const value = {
    currentUser,
    setCurrentUser,
    signup,
    login,
    loginWithEmail,
    signInWithGoogle,
    logout,

    // Calendar functions
    getCalendarEvents,
    createCalendarEvent,
    updateCalendarEvent,
    deleteCalendarEvent,

    // Expose token state
    googleAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;